<template>
  <DialogRoot v-model:open="dialogOpen" @update:open="handleCloseBuyVoucherModal">
    <DialogPortal>
      <Transition enter-active-class="backdrop-enter" leave-active-class="backdrop-leave">
        <DialogOverlay v-if="dialogOpen" class="modal-backdrop" />
      </Transition>

      <Transition enter-active-class="modal-enter" leave-active-class="modal-leave">
        <DialogContent v-if="dialogOpen" class="modal-content">
          <DialogTitle as="div" class="mb-6 lg:mb-10 flex justify-between items-start">
            <h4 class="text-[#3F3931]">{{ t('components.buyVoucherModal.title') }}</h4>

            <DialogClose asChild class="cursor-pointer size-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="text-accent">
                <path
                  d="M16.192 6.34424L11.949 10.5862L7.70697 6.34424L6.29297 7.75824L10.535 12.0002L6.29297 16.2422L7.70697 17.6562L11.949 13.4142L16.192 17.6562L17.606 16.2422L13.364 12.0002L17.606 7.75824L16.192 6.34424Z"
                  fill="currentColor"
                />
              </svg>
            </DialogClose>
          </DialogTitle>

          <DialogDescription class="sr-only"> </DialogDescription>

          <div class="h-full overflow-y-auto">
            <Form
              class="flex flex-col gap-8"
              :validation-schema="schema"
              @submit="handleSubmit"
              @invalidSubmit="hasSubmitted = true"
              @input="onFormTouch"
              v-slot="{ isSubmitting, meta }"
            >
              <input type="hidden" name="language" :value="locale" />
              <div class="form-control form-control__row flex flex-col gap-6 md:flex-row">
                <Field name="amount" v-slot="{ field, errorMessage, meta }" validateOnInput>
                  <SharedInput
                    :label="t('components.buyVoucherModal.amountLabel')"
                    :id="`${compoentId}-amount`"
                    name="amount"
                    type="number"
                    required
                    :error="meta.touched && errorMessage ? errorMessage : undefined"
                    :model-value="field.value"
                    @update:model-value="field.onInput"
                  />
                </Field>
              </div>

              <div class="form-control form-control__row flex flex-col gap-6 md:flex-row">
                <Field name="text" v-slot="{ field, errorMessage, meta }" validateOnInput>
                  <SharedTextarea
                    :label="t('components.buyVoucherModal.textLabel')"
                    :id="`${compoentId}-text`"
                    name="text"
                    type="text"
                    :error="meta.touched && errorMessage ? errorMessage : undefined"
                    :model-value="field.value"
                    @update:model-value="field.onInput"
                    :rows="3"
                  />
                </Field>
              </div>

              <Transition
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 transform translate-y-2"
                enter-to-class="opacity-100 transform translate-y-0"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 transform translate-y-0"
                leave-to-class="opacity-0 transform translate-y-2"
              >
                <div v-if="firstTimeSubmitted && formTouched && !validTurnstileToken">
                  <NuxtTurnstile v-model="turnstileToken" />
                </div>
              </Transition>

              <SharedButton
                class="w-max h-min"
                type="submit"
                :label="t('components.buyVoucherModal.submitButtonLabel')"
                :disabled="(hasSubmitted && !meta.valid && formTouched && !turnstileToken) || isSubmitting"
                :loading="isSubmitting"
              />
            </Form>
          </div>
        </DialogContent>
      </Transition>
    </DialogPortal>
  </DialogRoot>

</template>

<script setup lang="ts">
  import { useForm, Field, Form } from 'vee-validate';
  import { z } from 'zod';
  import { toTypedSchema } from '@vee-validate/zod';
  import type { BuyVoucherResponse } from '~/server/api/vouchers/buy';

  const compoentId = useId();

  const { t, locale } = useI18n();

  const schema = toTypedSchema(
    z.object({
      amount: z.number({ coerce: true }).min(1, t('components.buyVoucherModal.amountRequired')),
      text: z.string().optional(),
    })
  );

  const eventBus = useEventBus();

  const dialogOpen = ref(false);

  const { lockScroll, unlockScroll } = useScrollLock();

  const handleOpenBuyVoucherModal = () => {
    dialogOpen.value = true;
    lockScroll();
  };

  const handleCloseBuyVoucherModal = (v: boolean) => {
    if (!v) {
      dialogOpen.value = false;
      unlockScroll();
    }
  };

  eventBus.on('open-buy-voucher-modal', handleOpenBuyVoucherModal);

  onUnmounted(() => {
    eventBus.off('open-buy-voucher-modal', handleOpenBuyVoucherModal);
    unlockScroll();
  });

  const hasSubmitted = ref(false);
  const formTouched = ref(false);

  const onFormTouch = () => {
    formTouched.value = true;
  };

  const { validTurnstileToken, turnstileToken } = useValidTurnstileTokenCookie();

  const firstTimeSubmitted = ref(false);

  const handleSubmit = async (values: any, { resetForm }: { resetForm: () => void }) => {
    if (!firstTimeSubmitted.value) {
      firstTimeSubmitted.value = true;
    }
    if (hasSubmitted.value || !validTurnstileToken.value) {
      return;
    }
    hasSubmitted.value = true;
    const formValues = {
      ...values,
      language: locale.value,
      referrer: window.location.href,
    };

    try {
      const response = await fetch('/api/vouchers/buy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formValues),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const data: BuyVoucherResponse = await response.json();

      if (data.paymentUrl) {
        resetForm();
        handleCloseBuyVoucherModal(false);
        window.location.href = data.paymentUrl;
        return;
      }
    } catch (error) {
      eventBus.emit('add-global-toast', {
        show: true,
        title: t('components.buyVoucherModal.error'),
        message: t('components.buyVoucherModal.errorMessage'),
        type: 'foreground',
        error: true,
      });

      return;
    }
  };
</script>
